import { ddRum, rumUser } from '@web-platform/browser-rum';
import { fetchUserConfigurationAction } from 'actions/app-actions';
import { isEmpty as isEmptyUtil } from 'common/utils/data-utils';
import { login as utilLogin } from 'grab-login/lib/helper';
import { enableUIV2 } from 'helper/configuration';
import { getCompanyID } from 'helper/user';
import _endsWith from 'lodash/endsWith';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { of, AsyncSubject, throwError, combineLatest, EMPTY } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { GA_EVENT, GA_CATEGORY, GA_ACTION } from 'constants/ga-constants';
import { LOGIN_ERRORS, AUTHORIZATION_ACTIONS } from 'constants/login-constant';
import { enableEnterpriseApiDeprecation } from './feature-flag-helper';
import {
  linkCompanyAction,
  getCompanyUserProfilesAction,
  getUserNameAction,
  getCompanyPermissionsAction,
  fetchUserInfoAction,
} from './login-actions';

const LAST_LOGIN_TIME_STORAGE = '__LAST_LOGIN_TIME__';
export const SESSION_TIMEOUT_TIMES_STORAGE = '__SESSION_TIMEOUT_TIMES__';

const pushGALoginEvent = (safeID, companyID) => {
  const lastLoginTime = window.localStorage.getItem(LAST_LOGIN_TIME_STORAGE);
  const geFirstLogin =
    !lastLoginTime || moment().diff(moment(lastLoginTime), 'days') > 0;
  const sessionTimeoutTimes =
    Number(window.localStorage.getItem(SESSION_TIMEOUT_TIMES_STORAGE)) || 0;

  if (geFirstLogin) {
    window.localStorage.setItem(SESSION_TIMEOUT_TIMES_STORAGE, 0);
  }
  window.localStorage.setItem(LAST_LOGIN_TIME_STORAGE, moment().valueOf());
  window.pushGAEvent({
    event: GA_EVENT.GE_LOGIN_SUCCESS,
    category: GA_CATEGORY.TRANSACTION,
    action: GA_ACTION.LOGIN_SUCCESS,
    geFirstLogin,
    geSessionTimeoutTimes: geFirstLogin ? 0 : sessionTimeoutTimes,
    geLoginDate: moment().format('YYYY-MM-DD'),
    paxSafeID: safeID,
    geCompanyID: companyID,
  });
};

export const processLoginTokenAction = (token, callback) => dispatch => {
  /** Internal helper method to dispatch thunk actions while hooking onto the callback as observable */
  const dispatchWithCallbackAsObservable = (
    thunkWithCallbackAction,
    ...args
  ) => {
    const result = new AsyncSubject();

    dispatch((innerDispatch, ...dispatchArgs) => {
      // wrappedDispatch is to capture error returned from fetchHelper (API response error, timeout etc...)
      const wrappedDispatch = action => {
        if (typeof action === 'function') {
          return action(wrappedDispatch, ...dispatchArgs);
        }

        const defaultDispatchResult = innerDispatch(action);

        // _ERROR is generated in action-helper.js and dispatched by the fetchHelper
        if (
          typeof action === 'object' &&
          action.type &&
          _endsWith(action.type, '_ERROR')
        ) {
          // Return the action.type as the error message
          result.next(new Error(action.type));
          result.complete();
        }

        return defaultDispatchResult;
      };

      wrappedDispatch(
        thunkWithCallbackAction(...args, data => {
          // Problem with fetchHelper callback is that it will only callback when it is success
          // Hence the wrappedDispatch is added to listen to _ERROR action type to capture fetchHelper errors as well
          result.next(data);
          result.complete();
        }),
      );
    });

    return result.pipe(
      switchMap(data => {
        // This error should be returned from the warppedDispatch above, hence we throw the error in the stream
        if (data && data instanceof Error) {
          return throwError(data.message);
        }
        return of(data);
      }),
    );
  };

  of(token)
    .pipe(
      switchMap(token => {
        const useGrabExpressCompanyUserProfiles = enableEnterpriseApiDeprecation();
        if (useGrabExpressCompanyUserProfiles) {
          return combineLatest(
            of(token),
            dispatchWithCallbackAsObservable(
              getCompanyUserProfilesAction,
              token,
            ),
          );
        }

        // TODO: deprecate after flag rollout
        return combineLatest(
          of(token),
          dispatchWithCallbackAsObservable(fetchUserInfoAction, token),
        );
      }),
      switchMap(([token, userInfoData]) => {
        const companies = _get(userInfoData, 'companies', []);
        if (_isEmpty(companies)) {
          return throwError(LOGIN_ERRORS.noAssociatedCompany);
        }
        const companyID = getCompanyID(userInfoData);
        if (isEmptyUtil(companyID)) {
          return throwError(LOGIN_ERRORS.isLinkingFailed);
        }

        return combineLatest(
          of(token),
          of(companyID),
          dispatchWithCallbackAsObservable(getUserNameAction, token),
        );
      }),
      switchMap(([token, companyID, userNameData]) => {
        const { user } = userNameData;
        if (isEmptyUtil(user)) {
          return throwError(LOGIN_ERRORS.noUserFound);
        }

        pushGALoginEvent(user.safeID, companyID);

        return combineLatest(
          of(token),
          of(companyID),
          dispatchWithCallbackAsObservable(linkCompanyAction, companyID, token),
        );
      }),
      switchMap(([token, companyID, linkCompanyData]) => {
        const { isSuccessful } = linkCompanyData;

        if (!isSuccessful) {
          return throwError(LOGIN_ERRORS.isLinkingFailed);
        }

        // V2
        if (enableUIV2()) {
          utilLogin(token);
          return of({
            companyID,
          });
        }
        // V1
        return dispatchWithCallbackAsObservable(
          getCompanyPermissionsAction,
          token,
        ).pipe(
          switchMap(({ actions }) => {
            const isAdmin =
              actions.findIndex(
                permission => permission === AUTHORIZATION_ACTIONS.GE_ADMIN,
              ) !== -1;
            const canViewBooking =
              actions.findIndex(
                permission =>
                  permission === AUTHORIZATION_ACTIONS.VIEW_WEB_BOOKING,
              ) !== -1;

            if (canViewBooking) {
              utilLogin(token);
              const userCtx = rumUser()
                .custom('companyID', companyID)
                .build();
              ddRum.setUser(userCtx);
              dispatch(fetchUserConfigurationAction(companyID));
            }

            return of({
              companyID,
              isAdmin,
              canViewBooking,
            });
          }),
        );
      }),
      switchMap(result => {
        if (callback) {
          callback(null, result);
        }
        return EMPTY;
      }),
      catchError(err => {
        // Note that this error includes fetchHelper errors (when server reponse not 2xx)
        if (callback) {
          callback(err, null);
        }
        return EMPTY;
      }),
    )
    .subscribe();
};

export default {};
